<script setup>
import { computed } from 'vue';

const props = defineProps({
  /** Semantic colour. */
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'success', 'warning', 'error', 'info', 'maroon', 'gold'].includes(value)
  },
  /** 'solid' = saturated fill, white text. 'subtle' = tinted fill, dark readable
   *  text (the ASU system colour pairs — used for status tags). */
  tone: {
    type: String,
    default: 'solid',
    validator: (value) => ['solid', 'subtle'].includes(value)
  },
  /** 'tag' matches the Figma status tag: 12px bold, 2px/8px padding. */
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['tag', 'small', 'medium', 'large'].includes(value)
  },
  /** Optional leading mdi icon, e.g. 'mdi-check'. */
  icon: {
    type: String,
    default: null
  },
  rounded: {
    type: Boolean,
    default: false
  }
});

const sizeClasses = computed(() => {
  const sizes = {
    tag: 'ui-badge--tag',
    small: 'px-2 py-1 text-caption',
    medium: 'px-3 py-1 text-body-2',
    large: 'px-4 py-2 text-body-1'
  };
  return sizes[props.size];
});

const badgeClasses = computed(() => [
  'ui-badge',
  'd-inline-flex',
  'align-center',
  'justify-center',
  sizeClasses.value,
  `ui-badge--${props.tone}-${props.variant}`,
  props.size === 'tag' ? null : 'font-weight-medium',
  {
    'rounded-pill': props.rounded,
    rounded: !props.rounded
  }
]);
</script>

<template>
  <span :class="badgeClasses">
    <v-icon v-if="icon" :size="props.size === 'tag' ? 14 : 16" class="me-1">{{ icon }}</v-icon>
    <slot />
  </span>
</template>

<style scoped>
.ui-badge {
  /* Base styles - layout handled by utility classes */
  white-space: nowrap;
  vertical-align: middle;
}

/* Figma status tag metrics */
.ui-badge--tag {
  padding: 2px 8px;
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
}

/* Solid tone — saturated fill */
.ui-badge--solid-default {
  background-color: #f1f1f1;
  color: #333;
}

.ui-badge--solid-success {
  background-color: #4caf50;
  color: white;
}

.ui-badge--solid-warning {
  background-color: #ff9800;
  color: white;
}

.ui-badge--solid-error {
  background-color: #f44336;
  color: white;
}

.ui-badge--solid-info {
  background-color: #2196f3;
  color: white;
}

.ui-badge--solid-maroon {
  background-color: var(--asu-primary-maroon, #8c1d40);
  color: white;
}

.ui-badge--solid-gold {
  background-color: var(--asu-primary-gold, #ffc627);
  color: #000;
}

/* Subtle tone — ASU system colour pairs */
.ui-badge--subtle-default {
  background-color: var(--asu-grayscale-gray-6, #e8e8e8);
  color: var(--asu-grayscale-gray-3, #747474);
}

.ui-badge--subtle-success {
  background-color: var(--system-colors-success-background, #e9f5db);
  color: var(--system-colors-success-text, #446d12);
}

.ui-badge--subtle-warning {
  background-color: var(--system-colors-warning-background, #ffeade);
  color: var(--system-colors-warning-text, #bd4800);
}

.ui-badge--subtle-error {
  background-color: var(--system-colors-error-background, #f7dddd);
  color: var(--system-colors-error-text, #b02a2a);
}

.ui-badge--subtle-info {
  background-color: var(--system-colors-info-background, #d6f0fa);
  color: var(--system-colors-info-text, #126877);
}

.ui-badge--subtle-maroon {
  background-color: #f3e3e8;
  color: var(--asu-primary-maroon, #8c1d40);
}

.ui-badge--subtle-gold {
  background-color: #fff4d6;
  color: #6b4e00;
}
</style>
